import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { getCoreRowModel } from '@tanstack/react-table';
import { Box, Typography } from '@mui/material';

// Services
import { getSightingsMap, getVisitSightings } from '../../../../services/visit.service';

// Types
import { VisitLogEntry } from '../../../../types/VisitLogEntry';
import { PaginatedResult } from '../../../../types/PaginatedResult';

// Utils and common components
import { makeVisitSightingColumns } from '../../../../utils/columns/makevisitsightingcolumn';
import { Table, useNewTable } from '../../../common/table/index';

const mapSightingToEntry = (r: any): VisitLogEntry => ({
  id: r.id,
  deviceId: r.deviceId,
  facilityId: r.facilityId ?? r.closestFacilityId ?? '',
  facilityName: r.facilityName ?? r.closestFacilityName ?? '',
  closestReaderImei: r.closestReaderImei ?? r.imei ?? '',
  closestReaderName: r.closestReaderName ?? r.readerName ?? '',
  firstSeenDateTime: r.firstSeenDateTime ?? '',
  lastSeenDateTime: r.lastSeenDateTime ?? '',
  rssi: r.rssi ?? 0,
  temperatureF: r.temperatureF ?? 0,
  temperatureC: r.temperatureC ?? 0,
  humidity: r.humidity ?? 0,
  batteryPercent: r.batteryPercent ?? 0,
  location: r.location,
  locationMethodDescr: r.locationMethodDescr ?? '',
  locationMethod: r.locationMethod,
  visitId: r.visitId ?? '',
  imei: r.imei ?? '',
  readerName: r.readerName ?? '',
});

const resolveSightingsPage = (res: PaginatedResult<VisitLogEntry>): PaginatedResult<VisitLogEntry> => ({
  ...res,
  results: (res?.results ?? []).map((r: any) => mapSightingToEntry(r)),
});

/**
 * plotted sighting points come from the same page of results the table renders
 * getVisitSightings, not from the separate /map endpoint — that endpoint doesn't
 * reliably paginate in lockstep with /SightingProcessed once a visit spans more than
 * one page, which broke both the "#" numbering and the row<->marker selection sync.
 *
 */
const toMapPoints = (entries: VisitLogEntry[], totalCount: number, skip: number): VisitLogEntry[] =>
  entries
    .map((r, i) => ({ ...r, stepNumber: Math.max(totalCount - (skip + i), 1) }))
    .filter((r) => r.location?.latitude != null && r.location?.longitude != null);

interface VisitSightingsPanelProps {
  visitId: string;
  facilityId?: string | null;
  closestFacilityLogId?: string | null;
  firstSeenDateTime?: string | null;
  lastSeenDateTime?: string | null;
  activeIdx?: number | null;
  onActiveIdxChange?: (idx: number | null) => void;
  onDataChange?: (data: VisitLogEntry[]) => void;
  onLoadingChange?: (loading: boolean) => void;
  onErrorChange?: (hasError: boolean) => void;
}

export default function VisitSightingsPanel({
  visitId,
  closestFacilityLogId,
  activeIdx = null,
  onActiveIdxChange,
  onDataChange,
  onLoadingChange,
  onErrorChange,
}: VisitSightingsPanelProps) {
  const tableContainerRef = useRef<HTMLDivElement>(null);

  // Latest server total, read lazily by the "#" column
  const totalRowsRef = useRef(0);

  // Map state (derived from the same getVisitSightings page the table renders)
  const [mapPoints, setMapPoints] = useState<VisitLogEntry[]>([]);
  const [mapLoading, setMapLoading] = useState(true);
  const [mapError, setMapError] = useState(false);
  const mapRequestRef = useRef(0);
  const mapPointsRef = useRef<VisitLogEntry[]>([]);

  const baseFilters = useMemo(
    () => [{ VisitId: visitId }, ...(closestFacilityLogId ? [{ closestFacilityLogId }] : [])],
    [visitId, closestFacilityLogId],
  );

  const fetchAsync = useCallback(
    (requestParams: Record<string, any>): Promise<PaginatedResult<VisitLogEntry>> => {
      const { skip, take, sortBy } = requestParams;

      // One params object -> table and map always request the exact same page
      const params = {
        filters: baseFilters,
        sortBy: Array.isArray(sortBy) && sortBy.length > 0 ? sortBy : [{ id: -1 }],
        skip,
        take,
      };

      // Only the latest request may update state
      const requestId = ++mapRequestRef.current;

      // Drop the previous page's points immediately so a click can never resolve against stale data
      mapPointsRef.current = [];
      setMapPoints([]);
      setMapLoading(true);
      setMapError(false);

      // Kept for its reader location data; no longer used to build the plotted sighting
      // points (see toMapPoints comment above).
      getSightingsMap(params).catch((e) => console.error('Failed to fetch sightings map readers:', e));

      const sightingsPromise = getVisitSightings(params) as unknown as Promise<PaginatedResult<VisitLogEntry>>;

      sightingsPromise
        .then((res) => {
          if (requestId !== mapRequestRef.current) return;
          const resolved = resolveSightingsPage(res);
          setMapPoints(toMapPoints(resolved.results, resolved.totalCount ?? resolved.results.length, skip ?? 0));
        })
        .catch((e) => {
          if (requestId !== mapRequestRef.current) return;
          console.error('Failed to resolve sightings for map:', e);
          setMapPoints([]);
          setMapError(true);
        })
        .finally(() => {
          if (requestId === mapRequestRef.current) setMapLoading(false);
        });

      return sightingsPromise;
    },
    [baseFilters],
  );

  // Table isn't created yet at this point, so resolve the clicked row via a ref
  const handleRowClick = useCallback(
    (row: VisitLogEntry) => {
      const idx = mapPointsRef.current.findIndex((e) => e.id === row.id);

      onActiveIdxChange?.(idx >= 0 ? idx : null);
    },
    [onActiveIdxChange],
  );

  const columns = useMemo(() => makeVisitSightingColumns(() => totalRowsRef.current), []);

  const tableRefreshKey = useMemo(
    () => ({
      visitId,
      closestFacilityLogId: closestFacilityLogId ?? null,
    }),
    [visitId, closestFacilityLogId],
  );

  const table = useNewTable<VisitLogEntry>({
    columns,
    fetchAsync,
    resolveData: resolveSightingsPage,
    params: tableRefreshKey,
    serverSide: true,
    shouldFetch: Boolean(visitId),
    onRowClick: handleRowClick,
    initialState: { pagination: { pageIndex: 0, pageSize: 100 } },
  });

  // Must run during render so the "#" cells see the fresh total
  totalRowsRef.current = table.totalRows ?? 0;
  mapPointsRef.current = mapPoints;

  // Push map data.. status up to the parent
  useEffect(() => {
    onDataChange?.(mapPoints);
    // A new page/point set invalidates any previous selection (indices would point at other records)
    onActiveIdxChange?.(null);
  }, [mapPoints]);

  useEffect(() => {
    onLoadingChange?.(mapLoading);
  }, [mapLoading, onLoadingChange]);

  useEffect(() => {
    onErrorChange?.(mapError);
  }, [mapError, onErrorChange]);

  // Resolve the highlighted row by id so it survives re-sorting
  const activeId = activeIdx !== null ? (mapPoints[activeIdx]?.id ?? null) : null;

  useEffect(() => {
    if (!activeId || !tableContainerRef.current) return;

    tableContainerRef.current.querySelector(`[data-sighting-id="${activeId}"]`)?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
  }, [activeId]);

  return (
    <Box
      sx={{
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <Typography variant='body2' sx={{ px: 2, py: 1 }}>
        {`Showing ${table.data?.length ?? 0} of ${table.totalRows ?? 0} results`}
      </Typography>

      <Box
        ref={tableContainerRef}
        sx={{
          flex: 1,
          minHeight: 0,
          overflow: 'hidden',

          '& .ui-table': {
            height: '100%',
            minHeight: 0,
            display: 'flex',
            flexDirection: 'column',
          },

          '& .ui-table__card': {
            flex: '1 1 auto',
            minHeight: 0,
            display: 'flex',
            flexDirection: 'column',
          },

          '& .ui-table__scroll': {
            flex: '1 1 auto',
            minHeight: 0,
            maxHeight: 'none !important',
            overflowY: 'auto !important',
            overflowX: 'auto !important',
          },

          '& .ui-table__pagination': {
            flex: '0 0 auto',
          },

          ...(activeId && {
            [`& tbody tr[data-sighting-id="${activeId}"]`]: {
              backgroundColor: '#ede9fe !important',
              outline: '2px solid #7c3aed',
              outlineOffset: '-2px',
            },
          }),
        }}
      >
        <Box
          sx={{
            background: '#fff',
            borderRadius: 2,
            boxShadow: 1,
            height: '100%',
            minHeight: 0,
            display: 'flex',
            flexDirection: 'column',

            '& .MuiTableCell-root': {
              py: 0.625,
              px: 1.25,
            },
          }}
        >
          <Table
            {...table}
            showSearchBar={false}
            showColumnToggler={false}
            showAction={false}
            getCoreRowModel={getCoreRowModel()}
            rowProps={(row: { original: VisitLogEntry }) => ({
              'data-sighting-id': row.original.id,
            })}
          />
        </Box>
      </Box>
    </Box>
  );
}
